import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { HolderOutlined } from '@ant-design/icons/lib';
import classNames from 'classnames';
import styled from 'styled-components';

import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { DISPLAY_VALUES, ORIENTATION_TYPE } from 'constants/charts';

import { DATA_DISPLAY_TYPE, DATA_RANGE_TYPE, PRICE_DISPLAY_TYPE } from './constants';
import WidgetContent from './WidgetContent';
import { WidgetContext } from './WidgetContext';
import WidgetHeader from './WidgetHeader';
import { GRAPH_WIDGET_TYPES, SINGLE_METRIC_WIDGET_TYPES, WIDGET_TYPES } from '../constants';
import { WidgetGrouping, WidgetType } from '../types';
import { slugifyWidgetText } from '../utils';

const WidgetContainer = styled.div`
  display: flex;
  padding: 15px 20px;
  background: white;
  height: fit-content;
  border-radius: 16px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  width: 100%;
  min-height: 158px;

  &.graph,
  &.swot-score,
  &.swot-performance,
  &.reviews,
  &.reviews-summary,
  &.ai-insights,
  &.incentives,
  &.photos,
  &.amenities,
  &.pricing-comparison,
  &.contract-benefits,
  &.market-positioning {
    width: 585px;
    height: 340px;
  }

  &.map,
  &.amenities {
    .widget-drag-handle {
      top: 15px;
      left: 5px;
    }
  }
  &.map {
    height: 370px;
    padding: 0;
    background: none;
    box-shadow: none;
  }
  &.amenities {
    padding: 0;
  }

  :hover {
    .widget-drag-handle {
      display: block;
    }
    .widget-actions {
      visibility: visible;
    }
  }
`;

const Container = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
`;

const Handle = styled.div`
  position: absolute;
  display: none;
  cursor: pointer;
  left: -15px;
  top: -5px;
  width: 15px;
  height: 15px;
  &.map {
    top: 30px;
    z-index: 999;
  }
  svg {
    color: var(--text-secondary);
  }
`;

const Widget = forwardRef<HTMLDivElement, WidgetType>(
  ({ id, type, name, note, initial_settings, group_by, filters, isModalPreview }: WidgetType, ref) => {
    const { preview } = useCommunityViewContext();
    const elementRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => elementRef.current as HTMLDivElement);

    const [meta, setMeta] = useState<any>(undefined);
    const [rangeType, setRangeType] = useState<string>(initial_settings?.range || DATA_RANGE_TYPE.NOW);
    const [displayType, setDisplayType] = useState<string>(initial_settings?.format || DATA_DISPLAY_TYPE.GRAPH);
    const [orientationType, setOrientationType] = useState<string>(
      initial_settings?.orientation || ORIENTATION_TYPE.HORIZONTAL
    );
    const [displayValue, setDisplayValue] = useState<number>(
      initial_settings?.display_value ?? DISPLAY_VALUES.DISPLAY.value
    );
    const [priceDisplay, setPriceDisplay] = useState<number>(
      initial_settings?.price_display || PRICE_DISPLAY_TYPE.SINGLE
    );
    const [groupBy, setGroupBy] = useState<WidgetGrouping | undefined>(group_by);
    const [csvData, setCSVData] = useState<any>([]);

    const [showRemoveWidgetModal, setShowRemoveWidgetModal] = useState<boolean>(false);
    const [showEditWidgetModal, setShowEditWidgetModal] = useState<boolean>(false);

    const [filename, setFilename] = useState<string>('');

    useEffect(() => {
      if (initial_settings?.range) {
        setRangeType(initial_settings?.range);
      }
      if (initial_settings?.format) {
        setDisplayType(initial_settings?.format);
      }
      if (initial_settings?.orientation) {
        setOrientationType(initial_settings?.orientation);
      }
      if (initial_settings?.display_value !== undefined && initial_settings?.display_value !== null) {
        setDisplayValue(initial_settings.display_value);
      }
      if (initial_settings?.price_display) {
        setPriceDisplay(initial_settings.price_display);
      }
      if (group_by) {
        setGroupBy(group_by);
      }
    }, [initial_settings, setRangeType, setDisplayType, group_by, setGroupBy]);
    const widgetTitleSlug = slugifyWidgetText(name);
    const widgetTestId = widgetTitleSlug ? `widget-${widgetTitleSlug}` : `widget-${type}`;

    return (
      <WidgetContainer
        className={classNames('widget-container', {
          'single-metric': SINGLE_METRIC_WIDGET_TYPES.includes(type),
          graph: GRAPH_WIDGET_TYPES.includes(type),
          reviews: type === WIDGET_TYPES.REVIEWS,
          'reviews-summary': type === WIDGET_TYPES.REVIEWS_SUMMARY,
          'ai-insights': type === WIDGET_TYPES.AI_INSIGHTS,
          incentives: type === WIDGET_TYPES.INCENTIVES,
          photos: type === WIDGET_TYPES.PHOTOS,
          'swot-performance': type === WIDGET_TYPES.SWOT_PERFORMANCE,
          'swot-score': type === WIDGET_TYPES.SWOT_SCORE,
          map: type === WIDGET_TYPES.MAP,
          amenities: type === WIDGET_TYPES.AMENITIES,
          'contract-benefits': type === WIDGET_TYPES.CONTRACT_BENEFITS,
          'market-rate-graph': type === WIDGET_TYPES.MARKET_RATE_GRAPH,
          'pricing-comparison': type === WIDGET_TYPES.PRICING_COMPARISON,
          'market-positioning': type === WIDGET_TYPES.MARKET_POSITIONING,
        })}
        data-test-id={widgetTestId}
        ref={elementRef}
      >
        <WidgetContext.Provider
          value={{
            id,
            type,
            name,
            note,
            filters,
            isModalPreview,
            meta,
            elementRef,
            rangeType,
            displayType,
            orientationType,
            filename,
            csvData,
            initialRange: rangeType,
            initialFormat: displayType,
            initialOrientation: orientationType,
            displayValue,
            priceDisplay,
            groupBy,
            setMeta,
            showRemoveWidgetModal,
            showEditWidgetModal,
            setRangeType,
            setDisplayType,
            setOrientationType,
            setDisplayValue,
            setPriceDisplay,
            setGroupBy,
            setFilename,
            setCSVData,
            setShowRemoveWidgetModal,
            setShowEditWidgetModal,
          }}
        >
          <Container>
            {!preview && (
              <Handle
                className={classNames('widget-drag-handle', {
                  map: type === WIDGET_TYPES.MAP,
                })}
              >
                <HolderOutlined />
              </Handle>
            )}
            <WidgetHeader />
            <WidgetContent />
          </Container>
        </WidgetContext.Provider>
      </WidgetContainer>
    );
  }
);

export default Widget;
